import { Delete, Edit, Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { Contact } from "@repo/server-api";
import { BillingInformationType, RuleFactoryType, AddressType } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import { useState, useMemo, useRef } from "react";

import {
	useDeleteBillingInformation,
	useCreateBillingInformation,
	useUpdateBillingInformation,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import ConformationDialog from "@repo/legacy/components/shared/dialogs/Conformation";

import DialogBilingInformation, {
	DialogBillingInformationRef,
} from "../dialogs/DialogBillingInformation";

export default function BillingInformation(
	props: Readonly<{
		contact: Contact | null;
		setContact: (contact: Contact | null) => void;
		ruleFactory: RuleFactoryType;
	}>,
) {
	const { t } = useTranslation();

	const dialogRef = useRef<DialogBillingInformationRef>(null);

	const billingInfos = useMemo(() => {
		return (
			props.contact?.additionalInformation
				?.filter(
					(info): info is BillingInformationType =>
						(info as BillingInformationType).__typename === "BillingInformation",
				)
				.map((b) => ({ ...b, key: Math.random() * 1000 })) || []
		);
	}, [props.contact]);

	const [deleteBillingInfoExecute] = useDeleteBillingInformation();
	const [createBillingInfoExecute] = useCreateBillingInformation();
	const [updateBillingInfoExecute] = useUpdateBillingInformation();

	const deleteBillingInfo = (id: string) => {
		if (!props.contact?.id || !id || id === "new") return;

		deleteBillingInfoExecute(props.contact.id, id).then(() => {
			if (!props.contact) return;

			const updatedAdditionalInfo = props.contact.additionalInformation.filter(
				(info) => info.id !== id,
			);

			props.setContact({
				...props.contact,
				additionalInformation: updatedAdditionalInfo,
			});
		});
	};

	const handleSave = async (billingInfo: BillingInformationType) => {
		if (!props.contact?.id) return;

		try {
			console.log(props.contact.additionalInformation);

			if (!billingInfo.id || billingInfo.id === "new" || billingInfo.id === "") {
				const response = await createBillingInfoExecute(props.contact.id, billingInfo);
				console.log("Create response:", response);

				if (response.data == null) {
					return;
				}

				const newBillingInfo = {
					...response.data.createBillingInformation.billingInformation,
					__typename: "BillingInformation" as const,
				};

				props.setContact({
					...props.contact,
					additionalInformation: [...props.contact.additionalInformation, newBillingInfo],
				});
			} else {
				const response = await updateBillingInfoExecute(props.contact.id, billingInfo);
				console.log("update response:", response);

				if (response.data == null) {
					return;
				}

				const updatedBillingInfo = {
					...response.data.updateBillingInformation.billingInformation,
					__typename: "BillingInformation" as const,
				};

				const updatedAdditionalInfo = props.contact.additionalInformation.map((info) =>
					info.id === updatedBillingInfo.id ? updatedBillingInfo : info,
				);

				console.log("Updated Additional Info:", updatedAdditionalInfo);

				props.setContact({
					...props.contact,
					additionalInformation: updatedAdditionalInfo,
				});
			}
		} catch (error) {
			console.error("Error saving billing information:", error);
		}
	};

	const columns: GridColDef<BillingInformationType>[] = [
		{
			field: "name",
			headerName: t("name"),
			width: 250,
			renderCell: (params: GridCellParams) => {
				return params.row.name;
			},
		},
		{
			field: "actions",
			headerName: t("actions"),
			sortable: false,
			flex: 1,
			width: 150,
			renderCell: (params: GridCellParams) => {
				const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);

				const handleYes = (id: string) => {
					deleteBillingInfo(id);
					setOpenDeleteConformation(false);
				};

				const openDelete = () => {
					setOpenDeleteConformation(true);
				};

				const openEdit = (b: BillingInformationType) => {
					dialogRef.current?.show(b);
				};

				return (
					<div>
						<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
							<Edit />
						</Button>
						<Button data-testid="delete-button" onClick={() => openDelete()}>
							<Delete />
						</Button>
						<ConformationDialog
							text={t("billing-infomation.delete-conformation")}
							setOpenDeleteConformation={setOpenDeleteConformation}
							openPopup={openDeleteConformation}
							handleYes={() => handleYes(params.row.id.toString())}
						/>
					</div>
				);
			},
		},
	];

	const clickOnAdd = () => {
		dialogRef.current?.show({
			key: Math.random() * 1000,
			id: "new",
			__typename: "BillingInformation",
			isSameAsShippingAddress: false,
			name: "",
			billingAddress: {
				type: AddressType.German,
				street: "",
				state: "",
				city: "",
				zipCode: "",
				country: "",
				name: "",
			},
			bankAccount: {
				bankName: "",
				iban: "",
				bic: "",
				clientReference: "",
				clientReferenceDate: null,
			},
			shippingAddress: {
				type: AddressType.German,
				street: "",
				state: "",
				city: "",
				zipCode: "",
				country: "",
				name: "",
			},
		});
	};

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<Box className="clear">
				<Button
					variant="contained"
					className="greenButton"
					onClick={() => clickOnAdd()}
					data-testid="add-billing-info-button"
				>
					{t("billing-infomation.new")} <AddIcon />
				</Button>
			</Box>
			<DialogBilingInformation ref={dialogRef} onSave={handleSave} contact={props.contact} />
			{billingInfos.length ? (
				<DataGridPremium
					getRowId={(row: BillingInformationType) => (row.key ? row.key : "")}
					rows={billingInfos}
					columns={columns}
					disableRowSelectionOnClick
					className="table"
					hideFooter={true}
				/>
			) : (
				""
			)}
		</Box>
	);
}
